import { useState } from "react";
import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import { FormGroup, Label, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import { CheckIfEmailIsOccupied } from "../../actions/teacherMutations";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import Select from "react-select";
import AddNewTeacherToClass from "../Modal/AddNewTeacherToClass";
import DataGrid from "../DataGrids/DataGrid";
import { v4 as uuid } from "uuid";
import { compareByName } from "../../Utilities/sortHelper";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

const CreateClassInProgramForm = ({
  values,
  errors,
  touched,
  isSubmitting,
  setFieldValue,
  toggleModal,
  newTeachersToAdd,
  setNewTeachersToAdd,
}) => {
  const intl = useIntl();
  const teachersToAdd = values.teachersToAdd;
  const teachersForAdding = values.teachers.filter(
    (teacher) => !teachersToAdd.some((added) => added.id === teacher.id),
  );
  const [addNewTeacherModalOpen, setAddNewTeacherModalOpen] = useState(false);
  const mutateCheckEmailTaken = useMutation({
    mutationFn: CheckIfEmailIsOccupied,
  });
  const handleChange = (selected) => {
    if (selected.value === "AddNewTeacher") {
      setAddNewTeacherModalOpen(true);
    } else {
      setFieldValue("teachersToAdd", [...teachersToAdd, selected]);
    }
  };

  const removeTeacherFromAddList = (teacher) => {
    if (newTeachersToAdd.some((nt) => nt.id === teacher.id)) {
      setNewTeachersToAdd(newTeachersToAdd.filter((t) => t.id !== teacher.id));
    } else {
      setFieldValue(
        "teachersToAdd",
        teachersToAdd.filter((t) => t.id !== teacher.id),
      );
    }
  };

  const handleAddNewTeacher = (values) => {
    toggleAddTeacherModal();
    mutateCheckEmailTaken.mutate(
      {
        newEmail: values.email,
      },
      {
        onSuccess: (data) => {
          if (data.data === true) {
            toast.warn(<FormattedMessage id="EmailOccupied" />);
          } else {
            setNewTeachersToAdd([
              ...newTeachersToAdd,
              { id: uuid(), ...values },
            ]);
          }
        },
        onError: (err) => {
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      },
    );
  };

  const teachersDataSource = [...teachersToAdd, ...newTeachersToAdd].map(
    (teacher) => ({
      uniqueId: teacher.id,
      name: teacher.name,
      email: teacher.email,
      fullAccess: teacher.fullAccess ? (
        <FormattedMessage id="true" />
      ) : (
        <FormattedMessage id="false" />
      ),
      purchaser: teacher.purchaser ? (
        <FormattedMessage id="true" />
      ) : (
        <FormattedMessage id="false" />
      ),
      edit: (
        <Button
          color="danger"
          outline
          size="sm"
          onClick={() => removeTeacherFromAddList(teacher)}
        >
          <FontAwesomeIcon icon="trash" />
        </Button>
      ),
    }),
  );

  const toggleAddTeacherModal = () => {
    setAddNewTeacherModalOpen((prev) => !prev);
  };

  const teachersTableColumns = [
    { id: "edit", key: "edit", header: " ", width: 60, sortable: false },
    {
      id: "name",
      key: "name",
      header: () => <FormattedMessage id="Name" />,
      width: 180,
      sortable: false,
    },
    {
      id: "email",
      key: "email",
      header: () => <FormattedMessage id="Email" />,
      width: 200,
      sortable: false,
    },
    {
      id: "fullAccess",
      key: "fullAccess",
      header: () => <FormattedMessage id="FullTeacherAccess" />,
      width: 180,
      sortable: false,
    },
    {
      id: "purchaser",
      key: "purchaser",
      header: () => <FormattedMessage id="Purchaser" />,
      width: 150,
      sortable: false,
    },
  ];

  const selectableTeachers = () => {
    // Sortera före unshift så att "Lägg till ny lärare" ligger kvar överst.
    let teachers = teachersForAdding.sort(compareByName).map((option) => ({
      ...option,
      key: option.id,
      label: option.name,
      value: option.id,
    }));
    teachers.unshift({
      label: intl.formatMessage({ id: "AddNewTeacher" }),
      value: "AddNewTeacher",
    });
    return teachers;
  };

  return (
    <div className="col-12 col-sm-12">
      <Form>
        <FormGroup>
          <Label>
            <FormattedMessage id="ClassName" />
          </Label>
          <Field
            className={`form-control ${
              errors.name && touched.name && "is-invalid"
            }`}
            type="input"
            name="name"
            placeholder={intl.formatMessage({ id: "ClassName" })}
          />
          {errors.name && touched.name && (
            <div className="invalid-feedback">{errors.name}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="selectTeachers">
            <FormattedMessage id="SelectOneOrMoreTeachers" />
          </Label>
          <Select
            placeholder={<FormattedMessage id="ChooseTeacherPlaceholder" />}
            isMulti={false}
            noOptionsMessage={() => (
              <FormattedMessage id="NoSelectableTeachersInClass" />
            )}
            onChange={(selected) => handleChange(selected)}
            options={selectableTeachers()}
          />
        </FormGroup>
        {teachersDataSource.length > 0 ? (
          <DataGrid columns={teachersTableColumns} rows={teachersDataSource} />
        ) : (
          ""
        )}

        <Button
          type="submit"
          outline
          color="primary"
          className="btn float-end"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <FormattedMessage id="Wait" />
          ) : (
            <FormattedMessage id="Add" />
          )}
        </Button>
        <Button
          type="button"
          outline
          color="secondary"
          onClick={toggleModal}
          className="float-end me-2"
        >
          <FormattedMessage id="Cancel" />
        </Button>
      </Form>
      <AddNewTeacherToClass
        isOpen={addNewTeacherModalOpen}
        toggle={toggleAddTeacherModal}
        onSubmit={handleAddNewTeacher}
        fullAccessTeacher={true}
        purchaserTeacher={true}
      />
    </div>
  );
};

const FormikForm = withFormik({
  mapPropsToValues: (props) => ({
    name: "",
    teachers: props.teachers,
    teachersToAdd: [],
  }),
  validationSchema: Yup.object().shape({
    name: Yup.string()
      .required(
        <FormattedMessage id="Views.Teacher.CreateClassForm.Error.RequireName" />,
      )
      .matches(
        /^[A-Za-z0-9,\-,", Å,å,Ä,ä,Ö,ö ]+$/,
        <FormattedMessage id="InvalidClassNameError" />,
      ),
  }),

  handleSubmit(values, { props }) {
    props.handleAddedClass({
      name: values.name,
      orientation: { id: 0, name: "" },
      teachers: values.teachersToAdd.map((teacher) => teacher.id),
    });
  },
})(CreateClassInProgramForm);

export default FormikForm;
